import { useContext } from "react";
import { Divider, Flex, Dropdown } from "antd";
import Container from "../container";
import styles from "./header.module.css";
import Search from "../Search/search";
import LocationSelector from "../LocationSelector/locationSelector";
import HomePageLogoLink from "./HomePageLogoLink";
import CMSLink from "../common/CMSLink";
import MobileLocationTrigger from "./MobileLocationTrigger";
import { locationcontext } from "@/lib/context";
import dynamic from "next/dynamic";

const HeaderLogin = dynamic(() => import("./login"), { ssr: false });
const NavDrawer = dynamic(() => import("./NavDrawer"), { ssr: false });

const NAV_VISIBLE_LIMIT = 4;

const CITY_PLACEHOLDER = /^\/city(\/|$)/;

function resolveCityLink(item, citySlug) {
  if (!item?.url || !citySlug) return item;

  const isNested = typeof item.url === "object" && item.url !== null;
  const rawUrl = isNested ? item.url.url : item.url;

  if (typeof rawUrl !== "string" || !CITY_PLACEHOLDER.test(rawUrl)) {
    return item;
  }

  const resolvedUrl = rawUrl.replace(
    CITY_PLACEHOLDER,
    (_, sep) => `/${citySlug}${sep}`,
  );

  return isNested
    ? { ...item, url: { ...item.url, url: resolvedUrl } }
    : { ...item, url: resolvedUrl };
}

export default function Header({ data }) {
  const { location } = useContext(locationcontext);

  if (!data || !data?.header) return null;

  const headerData = data.header;
  const citySlug = (location || "ahmedabad").toLowerCase();
  const navigation = (headerData?.navigation || []).map((item) =>
    resolveCityLink(item, citySlug),
  );
  const secondaryNavItem = navigation.find((item) => item?.is_secondary);
  const primaryNavigation = navigation.filter((item) => !item?.is_secondary);

  const visibleNav = primaryNavigation.slice(0, NAV_VISIBLE_LIMIT);
  const overflowNav = primaryNavigation.slice(NAV_VISIBLE_LIMIT);

  const overflowItems = overflowNav.map((item, i) => ({
    key: i,
    label: (
      <CMSLink config={item} useIn="header" className={styles.dropdownLink} />
    ),
  }));

  return (
    <header className={`${styles.header} !backdrop-blur-[15px] `}>
      <Container>
        <Flex align="center" className={styles.headerContainer}>
          {/* ── Left: Logo + Location ── */}
          <div className={styles.leftGroup}>
            <HomePageLogoLink logo={headerData?.logo?.[0]} />
            <div className={styles.locationMainRow}>
              <LocationSelector
                config={headerData?.location_selector}
                hideLabel
              />
            </div>
          </div>

          {/* ── Center: Nav links (lg+) ── */}
          {visibleNav.length > 0 && (
            <nav className={styles.navLinks}>
              {visibleNav.map((item, index) => (
                <CMSLink
                  key={index}
                  config={item}
                  useIn="header"
                  className={styles.navLink}
                />
              ))}
              {overflowNav.length > 0 && (
                <Dropdown
                  menu={{ items: overflowItems }}
                  trigger={["click"]}
                  placement="bottomRight"
                >
                  <button
                    className={styles.moreBtn}
                    aria-label="More navigation links"
                  >
                    More
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                </Dropdown>
              )}
            </nav>
          )}

          {/* ── Right: Search + Secondary CTA + Login + Mobile triggers ── */}
          <div className={styles.rightGroup}>
            {/* Search — md+ */}
            <div className={styles.searchSlot}>
              <Search
                data={{ search_placeholders: headerData?.search_placeholders }}
              />
            </div>

            {/* Secondary nav CTA (e.g. Create Event) — lg+ */}
            {secondaryNavItem && (
              <div className={styles.lgUp}>
                <CMSLink
                  config={secondaryNavItem}
                  useIn="header"
                  className={styles.secondaryButton}
                />
              </div>
            )}

            {/* Login — sm+ */}
            <div className={styles.smUp}>
              <HeaderLogin data={data?.login} />
            </div>

            {/* xs/sm only: compact city pill */}
            <div className={styles.xsOnly}>
              <MobileLocationTrigger config={headerData?.location_selector} />
            </div>

            {/* Hamburger */}
            <NavDrawer navigation={navigation} />
          </div>
        </Flex>
      </Container>

      <div className={styles.smOnlySubBar}>
        <Container>
          <Flex
            align="center"
            justify="space-between"
            className={styles.subBarInner}
          >
            <span className={styles.subBarLabel}>
              {headerData?.location_selector?.intent_text}{" "}
            </span>
            <LocationSelector
              config={headerData?.location_selector}
              hideLabel
            />
          </Flex>
        </Container>
      </div>
    </header>
  );
}
